<template>
    <div id="resultsDetailsPage">
        <v-container class="my-2">
            <v-row class="mx-1">
                <v-col md="6" sm="8" cols="12">
                    <v-autocomplete
                        v-model="raceSelect"
                        :items="races"
                        outlined
                        dense
                        :loading="isLoading"
                        :search-input.sync="search"
                        hide-no-data
                        hide-selected
                        :label="$t('results.search_race')"
                        persistent-hint
                        :hint="$t('results.hint_search_race')"
                        @change="loadInitial"
                        @click="clickSearch"
                        @blur="clickSearch"
                        @focus="clickSearch"
                        return-object
                        :filter="customFilter"
                    >
                        <template v-slot:item="data">
                            <v-list-item-content >
                                <v-list-item-title>
                                    <span v-html="paintMatchingTerms(data.item.text, search.split(' ').filter(obj => obj !== ''))"></span>
                                </v-list-item-title>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>
            <v-row v-if="raceSelect" class="mx-1">
                <v-col cols="12">
                    <v-row class="d-none d-sm-flex">
                        <v-col cols="12" sm="4" xs="12">
                            <v-autocomplete
                                v-model="selectedParticipants"
                                :items="participantNames"
                                item-text="text"
                                item-value="value"
                                :label="$t('results.details.filter_participants')"
                                multiple hide-details
                                :outlined="true"
                                dense
                                v-on:input="updateFilters"
                                :no-data-text="$t('common.no_data')"
                            >
                                <template v-slot:selection="{ index }">
                                    <span v-if="index === 0" class="grey--text caption">
                                    ({{ $t("results.details.filter_selections") }}:
                                    {{ selectedParticipants.length }} )
                                    </span>
                                </template>
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" sm="4" xs="12">
                            <v-autocomplete
                                v-model="selectedClasses"
                                :items="classesNames"
                                item-text="text"
                                item-value="value"
                                :label="$t('results.details.filter_classes')"
                                multiple hide-details
                                :outlined="true"
                                v-on:input="updateClassFilters"
                                dense
                                :no-data-text="$t('common.no_data')"
                            >
                                <template v-slot:selection="{ index }">
                                    <span v-if="index === 0" class="grey--text caption">
                                    ({{ $t("results.details.filter_selections") }}:
                                    {{ selectedClasses.length }} )
                                    </span>
                                </template>
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" sm="4" xs="12">
                            <v-select
                                v-model="selectedBrands"
                                :items="brandNames"
                                item-text="text"
                                item-value="value"
                                :label="$t('results.details.filter_brands')"
                                multiple
                                :outlined="true"
                                dense hide-details
                                v-on:input="updateFilters"
                                :no-data-text="$t('common.no_data')"
                            >
                                <template v-slot:selection="{ index }">
                                    <span v-if="index === 0" class="grey--text caption">
                                    ({{ $t("results.details.filter_selections") }}:
                                    {{ selectedBrands.length }} )
                                    </span>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" sm="4" xs="12" v-if="bShowingGroups">
                            <v-select
                                v-model="selectedGroups"
                                :items="groupNames"
                                item-text="text"
                                item-value="value"
                                :label="$t('results.details.filter_groups')"
                                multiple
                                :outlined="true"
                                dense hide-details
                                v-on:input="updateFilters"
                                :no-data-text="$t('common.no_data')"
                            >
                                <template v-slot:selection="{ index }">
                                    <span v-if="index === 0" class="grey--text caption">
                                    ({{ $t("results.details.filter_selections") }}:
                                    {{ selectedGroups.length }} )
                                    </span>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" sm="4" xs="12">
                            <v-select
                                v-model="selectedSections"
                                :items="sectionItems"
                                item-text="text"
                                item-value="value"
                                :label="$t('results.details.filter_section_details')"
                                multiple
                                :outlined="true"
                                v-on:input="toggleDetails"
                                dense hide-details
                                :no-data-text="$t('common.no_data')"
                            >
                                <template v-slot:prepend-item>
                                    <v-list-item
                                        ripple
                                        @click="selectAllSections"
                                    >
                                        <v-list-item-action>
                                            <v-icon>
                                            {{ iconSection }}
                                            </v-icon>
                                        </v-list-item-action>
                                        <v-list-item-content>
                                            <v-list-item-title>
                                            {{ $t("races.select-all") }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-divider class="mt-2"></v-divider>
                                </template>
                                <template v-slot:selection="{ index }">
                                    <span v-if="index === 0" class="grey--text caption">
                                    ({{ $t("results.details.filter_selections") }}:
                                    {{ selectedSections.length }} )
                                    </span>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>
                    <v-row class="d-flex d-sm-none">
                        <v-expansion-panels class="my-2">
                            <v-expansion-panel>
                                <v-expansion-panel-header>{{$t('common.filters')}}</v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-row>
                                        <v-col cols="12" sm="4" xs="12">
                                            <v-autocomplete
                                                v-model="selectedParticipants"
                                                :items="participantNames"
                                                item-text="text"
                                                item-value="value"
                                                :label="$t('results.details.filter_participants')"
                                                multiple hide-details
                                                :outlined="true"
                                                dense
                                                v-on:input="updateFilters"
                                                :no-data-text="$t('common.no_data')"
                                            >
                                                <template v-slot:selection="{ index }">
                                                    <span v-if="index === 0" class="grey--text caption">
                                                    ({{ $t("results.details.filter_selections") }}:
                                                    {{ selectedParticipants.length }} )
                                                    </span>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="4" xs="12">
                                            <v-autocomplete
                                                v-model="selectedClasses"
                                                :items="classesNames"
                                                item-text="text"
                                                item-value="value"
                                                :label="$t('results.details.filter_classes')"
                                                multiple hide-details
                                                :outlined="true"
                                                v-on:input="updateClassFilters"
                                                dense 
                                                :no-data-text="$t('common.no_data')"
                                            >
                                                <template v-slot:selection="{ index }">
                                                    <span v-if="index === 0" class="grey--text caption">
                                                    ({{ $t("results.details.filter_selections") }}:
                                                    {{ selectedClasses.length }} )
                                                    </span>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="4" xs="12">
                                            <v-select
                                                v-model="selectedBrands"
                                                :items="brandNames"
                                                item-text="text"
                                                item-value="value"
                                                :label="$t('results.details.filter_brands')"
                                                multiple
                                                :outlined="true"
                                                dense hide-details
                                                v-on:input="updateFilters"
                                                :no-data-text="$t('common.no_data')"
                                            >
                                                <template v-slot:selection="{ index }">
                                                    <span v-if="index === 0" class="grey--text caption">
                                                    ({{ $t("results.details.filter_selections") }}:
                                                    {{ selectedBrands.length }} )
                                                    </span>
                                                </template>
                                            </v-select>
                                        </v-col>
                                        <v-col cols="12" sm="4" xs="12" v-if="bShowingGroups">
                                            <v-select
                                                v-model="selectedGroups"
                                                :items="groupNames"
                                                item-text="text"
                                                item-value="value"
                                                :label="$t('results.details.filter_groups')"
                                                multiple
                                                :outlined="true"
                                                dense hide-details
                                                v-on:input="updateFilters"
                                                :no-data-text="$t('common.no_data')"
                                            >
                                                <template v-slot:selection="{ index }">
                                                    <span v-if="index === 0" class="grey--text caption">
                                                    ({{ $t("results.details.filter_selections") }}:
                                                    {{ selectedGroups.length }} )
                                                    </span>
                                                </template>
                                            </v-select>
                                        </v-col>
                                        <v-col cols="12" sm="4" xs="12">
                                            <v-select
                                                v-model="selectedSections"
                                                :items="sectionItems"
                                                item-text="text"
                                                item-value="value"
                                                :label="$t('results.details.filter_section_details')"
                                                multiple
                                                :outlined="true"
                                                v-on:input="toggleDetails"
                                                dense hide-details
                                                :no-data-text="$t('common.no_data')"
                                            >
                                                <template v-slot:prepend-item>
                                                    <v-list-item
                                                        ripple
                                                        @click="selectAllSections"
                                                    >
                                                        <v-list-item-action>
                                                            <v-icon>
                                                            {{ iconSection }}
                                                            </v-icon>
                                                        </v-list-item-action>
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                            {{ $t("races.select-all") }}
                                                            </v-list-item-title>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                    <v-divider class="mt-2"></v-divider>
                                                </template>
                                                <template v-slot:selection="{ index }">
                                                    <span v-if="index === 0" class="grey--text caption">
                                                    ({{ $t("results.details.filter_selections") }}:
                                                    {{ selectedSections.length }} )
                                                    </span>
                                                </template>
                                            </v-select>
                                        </v-col>  
                                    </v-row>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-row>
                </v-col>
                <v-col cols="12" sm="4" xs="12">
                    <v-btn  @click="updateScores"
                        :color="`${buttonColor}`"
                        class="btn">
                        <v-icon left small>
                        fas fa-sync
                        </v-icon>
                        {{ $t("results.details.refresh") }}
                    </v-btn>
                </v-col>
            </v-row>
            <v-row v-if="raceSelect" class="mx-1">
                <v-col cols="12">
                    <VueTabulator v-if="!isModeSprint" ref="tabulator" :options="tabulatorOptions"  style="width: 100%" />
                    <VueTabulator v-else ref="tabulator" :options="tabulatorOptionsSingle"  style="width: 100%" />
                </v-col>
            </v-row>
            <Snack :snack="snackbar" :snackMsg="msg_snackbar" :snackCol="color_snackbar"/>
        </v-container>
    </div>
</template>

<script>
import { 
    VRow, VCol, VBtn, VSelect, VAutocomplete, VIcon, VContainer, VListItem, VListItemAction, VListItemContent, VDivider, VListItemTitle, VExpansionPanel,
    VExpansionPanels, VExpansionPanelHeader, VExpansionPanelContent
} from 'vuetify/lib';
import ResultsService from '@/services/ResultsService';
import RegisteredParticipantsService from '@/services/RegisteredParticipantsService';
import Snack from '@/components/notifications/Snack.vue';

export default {
    name:'ResultsDetails',
    components:{
        VRow, VCol, VBtn, VSelect, VAutocomplete, VIcon, VContainer, VListItem, VListItemAction, VListItemContent, VDivider, VListItemTitle, 
        VExpansionPanel, VExpansionPanels, VExpansionPanelHeader, VExpansionPanelContent, Snack
    },
    data(){
        return{
            selectedParticipants:[],
            participantNames:[],
            selectedSections:[],
            selectedGroups:[],
            groupNames:[],
            selectedBrands:[],
            brandNames:[],
            selectedClasses:[],
            buttonColor: "",
            raceInfo: null,
            raceClasses: [],
            raceColumns: [],
            raceSections: [],
            scoreHash: '',
            columns:[],
            raceSelect: null,
            races: [],
            isLoading:  false,
            search: '',
            lastSearch: '',
            snackbar: false,
            msg_snackbar: '',
            color_snackbar: '',
            tabulatorData: [],
            bandChange: false,
            filtersVisible: []
        }
    },
    created(){
        this.buttonColor = this.$store.getters.env.get('VUE_APP_BUTTON_COLOR');
    },
    mounted(){
        this.loadInitial();
        this.isMobile_();
    },
    computed: {
        visibleFields() {
            var visibleFields = [];
            for (var i = 0; i < this.tabulatorOptions.columns.length; i++) {
                if (this.tabulatorOptions.columns[i].visible) {
                visibleFields.push(this.tabulatorOptions.columns[i].field);
                }
            }

            return visibleFields;
        },
        classesNames: function () {
            var data = [];

            for (var i = 0; i < this.raceClasses.length; i++) {
                const cl = {
                text: this.raceClasses[i].name,
                value: this.raceClasses[i].name,
                };

                data.push(cl);
            }

            return data;
        },
        sectionItems: function () {
            var data = [];

            if (this.raceSections.length > 0) {
                for (var i = 0; i < this.raceSections.length; i++) {
                const section = {
                    text: this.raceSections[i].sectionName,
                    value: i,
                    runLimit: this.raceSections[i].runLimit,
                };

                data.push(section);
                }
            }

            return data;
        },
        raceImage: function () {
            return this.raceInfo.Race_Image == "0" || this.raceInfo.Race_Image == ""
                ? this.VUE_APP_DOMAIN +
                    "/admin/design/livelaps_com/images/orgPlaceholder.jpg"
                : this.VUE_APP_DOMAIN + this.raceInfo.Race_Image;
        },
        ad_600x90: function () {
            return this.raceInfo.ad_600x90 == "0" || this.raceInfo.ad_600x90 == ""
                ? this.VUE_APP_DOMAIN + "/design/livelaps_com/images/mainLogo_clear.png"
                : this.VUE_APP_DOMAIN + this.raceInfo.ad_600x90;
        },
        raceId() {
            if(this.raceSelect) {
                return this.raceSelect.raceId;
            }
            return undefined;
        },
        likesAllSection () {
            return this.selectedSections.length === this.sectionItems.length
        },
        likesSomeSection () {
            return this.selectedSections.length > 0 && !this.likesAllPart
        },
        iconSection () {
            if (this.likesAllSection) return 'far fa-check-square'
            if (this.likesSomeSection) return 'far fa-square'
            return 'far fa-square'
        },
        bShowingGroups() {
            return this.raceInfo ? this.raceInfo.isShowingGroup : false;
        },
        isModeSprint() {
            return this.raceInfo?.Race_Mode == 10 && this.raceInfo?.Race_Submode == 'sprint';
        },
        isShowingOverall() {
            return this.raceInfo?.isShowingOverall ?? false;
        },
        tabulatorOptionsSingle() {
            return {
                ajaxURL: `${this.$store.getters.env.get('VUE_APP_DEV_API_HOST')}/laravel/public/api/v1/livelaps/race/results/pro/${this.raceId}`,
                height: 500,
                downloadRowRange: "all",
                resizableColumns: false,
                ajaxResponse: this.tabulatorAjaxResponse,
                ajaxLoaderLoading: "<span>" + this.$t("results.loading") + "</span>",
                placeholder: '<span class="placeholderTabulator d-none">' + this.$t("results.no_data") + '</span>',
                dataLoaded: this.getResults,
                layout: "fitDataFill",
                pagination: false
            };
        },
        tabulatorOptions() {
            
            return {
                ajaxURL: `${this.$store.getters.env.get('VUE_APP_DEV_API_HOST')}/laravel/public/api/v1/livelaps/race/results/pro/${this.raceId}`,
                height: 500,
                downloadRowRange: "all",
                resizableColumns: false,
                ajaxResponse: this.tabulatorAjaxResponse,
                ajaxLoaderLoading: "<span>" + this.$t("results.loading") + "</span>",
                placeholder: '<span class="placeholderTabulator d-none">' + this.$t("results.no_data") + '</span>',
                dataLoaded: this.getResults,
                layout: "fitDataFill",
                pagination: false,
                ajaxProgressiveLoad: "scroll",
                ajaxProgressiveLoadDelay: 200
            };
        }
    },
    methods:{
        /**
         * Checks if a string contains all the given terms.
         *
         * @param {string} str - The input string to be checked.
         * @param {array} terms - An array of terms to check for in the string.
         * @return {boolean} Returns true if the string contains all the terms, false otherwise.
         */
        containAllTerms(str, terms) {
            if(!terms || terms.length === 0) {
                return false;
            }

            if(!str) {
                return false;
            }
            let response = true;

            str = str.toLowerCase();

            terms = terms.map(term => term.toLowerCase());

            terms.forEach(term => {
                if(!term){
                    return;
                }

                if(!str.includes(term)) {
                    response = false;
                }
            });

            return response;
        },
        customFilter (item, queryText) {
            const arraySearch = queryText.toLowerCase().replaceAll("\\","\\\\")
                                        .replaceAll("(", "\\(")
                                        .replaceAll(")", "\\)")
                                        .replaceAll(".", "\\.")
                                        .replaceAll("*", "\\*")
                                        .replaceAll("+", "\\+")
                                        .replaceAll("?", "\\?")
                                        .replaceAll("^", "\\^")
                                        .replaceAll("$", "\\$")
                                        .replaceAll("[", "\\]")
                                        .replaceAll("]", "\\]")
                                        .replaceAll("}", "\\}")
                                        .replaceAll("{", "\\{")
                                        .replaceAll("|", "\\|").split(' ');
            const regex = new RegExp(arraySearch.join('|'));

            return item.text.toLowerCase().search(regex) > -1
        },
        paintMatchingTerms(str, terms) {
            if(!terms || terms.length === 0) {
                return str;
            }
            let response = str;
            let expReg = terms.map((term) => {
                return term.replaceAll("\\","\\\\")
                    .replaceAll("(", "\\(")
                    .replaceAll(")", "\\)")
                    .replaceAll(".", "\\.")
                    .replaceAll("*", "\\*")
                    .replaceAll("+", "\\+")
                    .replaceAll("?", "\\?")
                    .replaceAll("^", "\\^")
                    .replaceAll("$", "\\$")
                    .replaceAll("[", "\\]")
                    .replaceAll("]", "\\]")
                    .replaceAll("}", "\\}")
                    .replaceAll("{", "\\{")
                    .replaceAll("|", "\\|");
            }).join('|');
                        
            let regex = new RegExp(expReg,"gi");
            response = str.replaceAll( regex, function(value) { return `<span style="color: #808080;">${value}</span>`; });

            return response;
        },
        async loadInitial() {
            this.bandChange = true;
            if(this.raceId) {
                await ResultsService.getRaceInfo(this.raceId).then((result) => {
                    this.raceInfo = result.data.message;
                });
                await ResultsService.getConfigRace(this.raceId).then((result) => {
                    this.raceClasses  = result.data.classes.data;
                    this.raceColumns  = result.data.columns.data;
                    this.raceSections = result.data.sections.data;
                });
                this.scoreHash = this.scoreHash = this.raceInfo.Score_Hash;
                this.setTabulatorColumns();
                let placeholder = document.getElementsByClassName('placeholderTabulator');
                if(placeholder.length > 0) {
                    placeholder = placeholder[0];
                    placeholder.classList.add("d-none");
                }
                const table = this.$refs.tabulator?.getInstance();
                /*table.replaceData(`${this.$store.getters.env.get('VUE_APP_DEV_API_HOST')}/laravel/public/api/v1/livelaps/race/results/pro/${this.raceId}`);
                table.redraw();*/
                setTimeout(() => { table.redraw(); }, 500);
                this.resetFilters();
                if(this.isModeSprint) {
                    this.expandSprintDefault();
                }
            }
        },
        async selectClassDefault(n){
            let _class = this.raceClasses.find(x => x.sortOrder == n);
            if(_class != undefined && _class.id > 0){
                this.selectedClasses.push(_class.name);
            }else{
                this.selectClassDefault(n+1);
            }
        },
        setTabulatorColumns(){
            var table = this.$refs.tabulator.getInstance();
            var colData = this.setColNames(this.raceColumns);
            this.innerHeight = window.innerHeight - 250;
            table.setHeight(this.innerHeight);
            table.setColumns(colData);
            table.setSort("overallPosition", "asc");
        },
        setColNames(columns){
            var colData = [];
            columns.forEach((col) => {
                const params = col.paramsTitle ? { ...col.paramsTitle } : {};
                col.title = this.$te(col.title) ? this.$t(col.title, params) : col.title;
                if (col.field == 'fullName') {
                    col.formatter = this.fullNameFormat
                    col.cellClick = this.clickFullName
                }
                if (col.sorterFunction) {
                    switch (col.sorterFunction) {
                        case 'sorterSection':
                            col.sorter = this.sorterSection
                        break
                        case 'sorterSprintSection':
                            col.sorter = this.sorterSprintSection
                        break
                        default:
                            col.sorter = this[col.sorterFunction]
                    }
                    delete col.sorterFunction
                }
                if (col.formatFunction) {
                    switch (col.formatFunction) {
                        case 'displayTime':
                            col.formatter = this.displayTime
                        break
                        case 'displayOATime':
                            col.formatter = this.displayOATime
                        break
                        case 'displayCBTime':
                            col.formatter = this.displayCBTime
                        break
                        case 'formatMinutes':
                            col.formatter = this.formatMinutes
                        break
                        case 'formatPenaltyMinutes':
                            col.formatter = this.formatPenaltyMinutes
                        break
                        case 'brandFormat':
                            col.formatter = this.brandFormat
                        break
                        case 'displaySectionTime':
                            col.formatter = this.displaySectionTime
                        break
                        case 'displaySectionTimeSingle':
                            col.formatter = this.displaySectionTimeSingle
                        break
                        case 'displaySectionTimeArray':
                            col.formatter = this.displaySectionTimeArray
                        break
                        case 'displayTcTime':
                            col.formatter = this.displayTcTime
                        break
                        case 'displayTcPenalty':
                            col.formatter = this.displayTcPenalty
                        break
                        case 'classNameFormat':
                            col.formatter = 'textarea'
                            col.resizable = true
                            col.cssClass = col.cssClass.replace('not-resizable','');
                            //col.maxWidth = 150
                        break
                        default:
                            col.formatter = this[col.formatFunction]
                    }

                    delete col.formatFunction
                }
                colData.push(col)
                if (col.columns) {
                    col.columns = this.setColNames(col.columns)
                }
                if(col.chklap) { delete col.chklap }
                if(col.sectionId) { delete col.sectionId }
                if(col.lapNumber) { delete col.lapNumber }
                if(col.paramsTitle) { delete col.paramsTitle; }
                if(col.translate) { delete col.translate; }
                if(col.type) { delete col.type; }
            });

            return colData;
        },
        sorterSection(a, b, aRow, bRow, column, dir) {
            if (!a.score && !b.score) return 0;
            
            if (!a.score) return dir === "asc" ? 1 : -1;
            if (!b.score) return dir === "asc" ? -1 : 1;
            
            var timeA = !this.validateTimeZero(a.score) ? this.timeToMs(a.score) : 0;
            var timeB = !this.validateTimeZero(b.score) ? this.timeToMs(b.score) : 0;
            
            return timeA > timeB ? 1 : timeA < timeB ? -1 : 0;
        },
        sorterSectionLapCheckpoint(a, b, aRow, bRow, column, dir) {
            const fieldName = column.getField();
            const fieldParts = fieldName.split('.');
            const aData = aRow.getData();
            const bData = bRow.getData();
            const aValue = aData[fieldParts[0]][fieldParts[1]]?.totalTimeArray || {};
            const bValue = bData[fieldParts[0]][fieldParts[1]]?.totalTimeArray || {};
            return this.sorterSection(aValue, bValue, aRow, bRow, column, dir);
        },
        sorterSprintSection(a, b) {
            let aValue = (a && a.totalTimeArray) ? a.totalTimeArray.score : a;
            let bValue = (b && b.totalTimeArray) ? b.totalTimeArray.score : b;
            let timeA = aValue && !this.validateTimeZero(aValue) ? this.timeToMs(aValue) : 0;
            let timeB = bValue && !this.validateTimeZero(bValue) ? this.timeToMs(bValue) : 0;
            if (timeA > timeB) {
                return 1
            } else if (timeA < timeB) {
                return -1
            } else {
                return 0
            }
        },
        timeToMs(time) {
            var calcTime = 0
            var multipliers = [1, 60, 3600]

            if (typeof time === 'string' && time != '' && time.includes(':')) {
                var timeArray = time.split(':')

                var arraySize = timeArray.length
                var m = 0
                for (let i = arraySize - 1; i >= 0; i--) {
                    if (m > 2) {
                        calcTime = 0
                    }
                    calcTime += Number(timeArray[i]) * multipliers[m]
                    m++
                }
            } else {
                calcTime = time == '' ? 0 : time
            }
            if (calcTime == 0) {
                calcTime = 99999999 //empty scores last
            }
            return calcTime
        },
        isMobile_(){
            this.isMobile = window.innerWidth < 1024;
        },
        updateFilters(){
            if (!this.$refs.tabulator) {
                return false;
            }

            var table = this.$refs.tabulator.getInstance();
            var data = table.getData();

            if (data.length == 0) {
                table.clearFilter();
            }

            if (this.anyFiltersActive()) {
                var participants = [];
                this.filterObj = {
                    selectedClasses: this.selectedClasses,
                    selectedParticipants: this.selectedParticipants,
                    selectedBrands: this.selectedBrands,
                    selectedGroups: this.selectedGroups,
                    selectedSections: this.selectedSections,
                    idRace: this.$route.params.id
                };
                localStorage.setItem("filter_obj_results", JSON.stringify(this.filterObj));
                this.addSelectedParticipants(participants, data);
                this.addParticipantsInSelectedClasses(participants, data);
                this.addParticipantsInSelectedBrands(participants, data);
                this.addParticipantsInSelectedGroups(participants, data);
                table.setFilter([
                    {
                        field: "id",
                        type: "in",
                        value: participants,
                    },
                ]);
            } else {
                table.clearFilter();
            }
        },
        updateClassFilters(){
            this.updateFilters();
            this.setBehindColumn();
        },
        //--------------------------
        displayTime(cell, formatterParams, onRendered) {
            var data = cell.getValue()
            if (data == '' || !data) {
                return ''
            }
            return this.formatTime(data, formatterParams, onRendered)
        },
        formatTime(data, formatterParams, onRendered, bandWithoutFormat = false) {
            if (
                data === undefined ||
                data === null ||
                data == '---' ||
                data.length == 0
            ) {
                return data
            }

            data = data + '' //make it a string

            var time = []
            if (data.includes('.')) {
                time = data.split('.')
            } else {
                time[0] = data
            }
            var ms = ''
            if (typeof time[1] != 'undefined' && (time[1] || "").length > 0) {
                ms = time[1].substring(0, 3)
            } else {
                ms = "000";
            }

            var a = time[0].split(':')
            var seconds = +a[0] * 60 * 60 + +a[1] * 60 + +a[2]
            
            if (this.timeFormatSelected == 0 || bandWithoutFormat) {
                ms = ms == '' ? '' : '.' + ms
                seconds = parseInt(seconds, 10) // don't forget the second param
                var hours = Math.floor(seconds / 3600)
                var minutes = Math.floor((seconds - hours * 3600) / 60)
                seconds = seconds - hours * 3600 - minutes * 60

                if (hours < 10) {
                hours = '0' + hours
                }
                if (minutes < 10) {
                minutes = '0' + minutes
                }
                if (seconds < 10) {
                seconds = '0' + seconds
                }

                if (isNaN(hours)) {
                hours = 0
                }
                if (isNaN(minutes)) {
                minutes = 0
                }
                if (isNaN(seconds)) {
                seconds = 0
                }
                time = hours + ':' + minutes + ':' + seconds + '' + ms
                
                return time
            }

            if (this.timeFormatSelected == 1) {
                seconds = parseInt(seconds, 10) // don't forget the second param
                minutes = Math.floor(seconds / 60)
                seconds = seconds - minutes * 60
                if (isNaN(minutes)) {
                minutes = 0
                }
                if (isNaN(seconds)) {
                seconds = 0
                }
                minutes = minutes + 'm '

                seconds = seconds + 's '
                time = minutes + '' + seconds + '' + ms
                return time
            }

            return data
        },
        displayOATime(cell, formatterParams, onRendered) {
            var data = cell.getValue()
            if (data == '---') {
                return this.getTranslation('races.oa_leader')
            } else {
                return this.formatTime(data, formatterParams, onRendered)
            }
        },
        getTranslation(text) {
            return this.$te(text) ? this.$t(text) : text;
        },
        displayCBTime(cell, formatterParams, onRendered) {
            var data = cell.getValue()
            if (data == '---') {
                return this.getTranslation('races.class_leader')
            } else {
                return this.formatTime(data, formatterParams, onRendered)
            }
        },
        formatMinutes(cell) {
            var seconds = cell.getValue()
            if (seconds === null || seconds.length == 0) {
                seconds = 0
            }
            seconds = parseInt(seconds, 10)
            return seconds / 60
        },
        formatPenaltyMinutes(cell) {
            var seconds = cell.getValue()
            if (typeof seconds == 'undefined') {
                seconds = 0
            }
            if (seconds === null || seconds.length == 0) {
                seconds = 0
            }
            const time = seconds;
            return time
        },
        brandFormat(cell) {
            var brand = cell.getValue()

            return (
                '<span class="' +
                brand.replace(' ', '').toLowerCase() +
                '">' +
                brand +
                '</span>'
            )
        },
        displaySectionTimeSingle(cell, formatterParams, onRendered) {
            let array = cell.getValue()
            //let column = cell.getColumn()
            //let data = cell.getData()
            var time = null;
            if (typeof array == 'undefined' || array['score'] == '' || this.validateTimeZero(array['score'])) {
                return ''
            }
            if (array['style'] != 'dsq' && !array['isThrown'] && array['style'] != 'incomplete') {
                time = this.formatTime(
                array['score'],
                formatterParams,
                onRendered
                )
            } else if (array['isThrown']) {
                time = "<i class='fas fa-ban fared'></i> <span class='fared'>" + this.formatTime(
                array['score'],
                formatterParams,
                onRendered
                ) + "</span>";
            } else if (array) {
                time = this.formatTime(
                    array['score'],
                    formatterParams,
                    onRendered
                );
            } else {
                time = ''
            }

            return time
        },
        displaySectionTimeSprintDetails(cell, formatterParams, onRendered) {
            var array = cell.getValue();
            if (typeof array == "undefined") {
                return "";
            }
            let time = "";
            let _style = "";
            
            if(array.totalTimeArray) {
                time = array.totalTimeArray['score'];
                if((time || "").length > 0) {
                    time = this.formatTime(
                        time,
                        formatterParams,
                        onRendered,
                        _style == 'incomplete'
                    );
                } else {
                    time = '';
                }
                
                _style = array.totalTimeArray['style'];
                switch(_style) {
                        case 'dsq': case 'incomplete': {
                            let _time = (array.totalTimeArray['score'] && array.totalTimeArray['score'] != '') ? array.totalTimeArray['score'].split('.')[0] : '';
                            if((time || "").length > 0) time = "<span class='" + _style + "'>(" + _time + ')</span>';
                            break;
                        }
                        case 'unscored':
                            time = "<span class='unscored'>" + time + "</span>";
                        break;
                        case 'thrown': case 'fared': case 'fagray':
                            time = "<span class='fared'>" + time + '</span>';
                        break;
                        default: 
                            time = "<span class='bold'>" + time + '</span>';
                        break;
                }
                let bandTimeZero = this.validateTimeZero(array.totalTimeArray['score']);
                if (((time || "").length > 0 || _style == 'incomplete') && !bandTimeZero && array.totalTimeArray['icon'] != '') {
                    var icon = "<i class='" + array.totalTimeArray['icon'] + "'></i>";
                    if (array.totalTimeArray['iconPosition'] == 'left') {
                        time = icon + '' + time
                    } else if (array.totalTimeArray['iconPosition'] == 'right') {
                        time = time + '' + icon
                    } else if (array.totalTimeArray['iconPosition'] == 'kill') {
                        time = icon
                    }
                }
                if(
                    (time || "").length > 0 && !bandTimeZero && (array.sectionOverallPosition && array.sectionOverallPosition != "" || 
                    array.sectionClassPosition && array.sectionClassPosition != "")
                ) {
                    if(time != '') time += '<br />';
                    time += '<span class="sectionPositionText">';
                    if (!isNaN(parseInt(array.sectionOverallPosition, 10)) && this.raceInfo.isShowingOverall) {
                        time += ' O(' + parseInt(array.sectionOverallPosition, 10) + ')'
                    }
                    if (!isNaN(parseInt(array.sectionClassPosition, 10))) {
                        time += ' C(' + parseInt(array.sectionClassPosition, 10) + ')'
                    }
                    time += '</span>';
                }
                if((time || "").length > 0 && !bandTimeZero && array.avgSpeedWithMeasure) {
                    time = time + '<br />' +
                        "<span class='sectionPositionText'>(" + 
                        array.avgSpeedWithMeasure +
                        ')</span> <br />';
                }
                if(_style == 'incomplete' && (time || "").length > 0) {
                    time = time + '<br />';
                }
            }
            return time;
        },
        displaySectionTime(cell, formatterParams, onRendered) {
            var array = cell.getValue()

            if (typeof array == 'undefined') {
                return ''
            }
            var time = null;
            if (array['style'] != 'dsq' && array['style'] != 'incomplete') {
                time = this.formatTime(array['score'], formatterParams, onRendered)
            } else {
                time = array['score']
            }

            if (this.raceInfo.Race_Mode == 6 || this.raceInfo.Race_Mode == 9) {
                if (array['classPosition'] != '' || array['overallPosition'] != '') {
                time += '<br>'
                time += "<span class='sectionPositionText'>"
                if (!isNaN(parseInt(array['overallPosition'], 10)) && this.raceInfo.isShowingOverall) {
                    time += ' O(' + parseInt(array['overallPosition'], 10) + ') '
                }
                if (!isNaN(parseInt(array['classPosition'], 10))) {
                    time += ' C(' + parseInt(array['classPosition'], 10) + ') '
                }
                time += '</span>'
                }
            } else {
                if (
                array['sectionClassPosition'] != '' ||
                array['sectionOverallPosition'] != ''
                ) {
                time += '<br>'
                time += "<span class='sectionPositionText'>"
                if (!isNaN(parseInt(array['sectionOverallPosition'], 10)) && this.raceInfo.isShowingOverall) {
                    time += ' O(' + parseInt(array['sectionOverallPosition'], 10) + ')'
                }
                if (!isNaN(parseInt(array['sectionClassPosition'], 10))) {
                    time += ' C(' + parseInt(array['sectionClassPosition'], 10) + ')'
                }
                time += '</span>'
                }
            }
            //just show empty cell
            if (time == '') {
                return time
            }

            if (array['style'] == 'dsq') {
                if (time) {
                time = "<span class='dsq'>(" + time + ')</span>'
                } else {
                time = ''
                }
            }

            if (array['style'] == 'incomplete') {
                if (time) {
                time = "<span class='incomplete'>(" + time + ')</span>'
                } else {
                time = ''
                }
            }

            if (array['style'] == 'unscored') {
                if (time) {
                time = "<p class='unscored'>" + time + '</p>'
                } else {
                time = ''
                }
            }

            if (array['style'].includes('thrown')) {
                time = "<span class='fared'>" + time + '</span>'
            }

            if (array['style'] == 'fared') {
                time = "<span class='fared'>" + time + '</span>'
            }

            if (array['icon'] != '') {
                var icon = "<i class='" + array['icon'] + "'></i>"
                if (array['iconPosition'] == 'left') {
                time = icon + '' + time
                } else if (array['iconPosition'] == 'right') {
                time = time + '' + icon
                } else if (array['iconPosition'] == 'kill') {
                time = icon
                }
            }
            return time
        },
        validateTimeZero(time) {
            if((time || '').length > 0) {
                let timeArray = time.split('.');
                let band = false;
                let allItemTimes = [];
                if(Array.isArray(timeArray)) {
                    timeArray.forEach((subTime) => {
                        let subTimeArray = subTime.split(':');
                        subTimeArray.forEach((itemTime) => {
                            allItemTimes.push(itemTime);
                        });
                    });
                    allItemTimes.forEach((item) => {
                        if(parseInt(item) != 0) {
                            band = true;
                        }
                    });
                    return !band;
                }
                return false;
            }
            return false;
        },
        displaySectionTimeArray(cell, formatterParams, onRendered) {
            var array = cell.getValue();
            //console.log('Details',array);
            if (typeof array == "undefined") {
                return "";
            }
            let time = "";
            array.forEach((el) => {
                let _time_value = '';
                let style = el.totalTimeArray['style'];
                let _time = el.totalTimeArray['score'];
                let bandTimeZero = this.validateTimeZero(el.totalTimeArray['score']);
                if((_time || "").length > 0) {
                    _time_value = _time = this.formatTime(
                        _time,
                        formatterParams,
                        onRendered,
                        style == 'incomplete'
                    );
                } else {
                    _time_value = _time = '';
                }
                switch(style) {
                        case 'dsq': case 'incomplete':
                            if((_time || "").length > 0) _time = "<span class='" + el.totalTimeArray['style'] + "'>(" + _time + ')</span>';
                        break;
                        case 'unscored':
                            _time = "<span class='unscored'>" + _time + "</span>";
                        break;
                        case 'thrown': case 'fared':
                            _time = "<span class='fared'>" + _time + '</span>';
                        break;
                        default: 
                            _time = "<span class='bold'>" + _time + '</span>';
                        break;
                }
                //console.log(((_time_value || "").length > 0 || style == 'incomplete'),!bandTimeZero,el.totalTimeArray['icon'] != '', el.totalTimeArray);
                if (((_time_value || "").length > 0 || style == 'incomplete') && !bandTimeZero && el.totalTimeArray['icon'] != '') {
                    var icon = "<i class='" + el.totalTimeArray['icon'] + "'></i>";
                    if (el.totalTimeArray['iconPosition'] == 'left') {
                        _time = icon + '' + _time
                    } else if (el.totalTimeArray['iconPosition'] == 'right') {
                        _time = _time + '' + icon
                    } else if (el.totalTimeArray['iconPosition'] == 'kill') {
                        _time = icon
                    }
                }
                if(
                    (_time_value || "").length > 0 && !bandTimeZero && el.sectionOverallPosition && el.sectionOverallPosition != "" && 
                    el.sectionClassPosition && el.sectionClassPosition != ""
                ) {
                    if(_time != '') _time += '<br />';
                    _time += '<span class="sectionPositionText">';
                    if (!isNaN(parseInt(el.sectionOverallPosition, 10)) && this.raceInfo.isShowingOverall) {
                        _time += ' O(' + parseInt(el.sectionOverallPosition, 10) + ')'
                    }
                    if (!isNaN(parseInt(el.sectionClassPosition, 10))) {
                        _time += ' C(' + parseInt(el.sectionClassPosition, 10) + ')'
                    }
                    _time += '</span>';
                }
                if((_time_value || "").length > 0 && !bandTimeZero) {
                    time += _time + '<br />' +
                        "<span class='sectionPositionText'>(" +
                        el.avgSpeed +
                        ' ' +
                        el.avgSpeedMeasure +
                        ')</span> <br />';
                }
                if(style == 'incomplete' && (_time || "").length > 0) {
                    time += _time + '<br />';
                }
            });
            return time
        },
        displayTcTime(cell, formatterParams, onRendered) {
            var array = cell.getValue()

            if (typeof array == 'undefined') {
                return 0
            }

            var time = array['score']

            if (!time) {
                time = 0
            }

            if (array['style'] == 'dsq') {
                if (time) {
                time = "<span class='dsq'>(" + time + ')</span>'
                } else {
                time = ''
                }
            } else {
                if (array['style'] == 'early_dsq') {
                time = "<span class='dsq'>" + time + ' E</span>'
                }
                if (array['style'] == 'late_dsq') {
                time = '' + time + ''
                }
                if (array['style'] == 'fared') {
                time = "<span class='fared'>" + time + '</span>'
                }

                if (array['style'] == 'mr') {
                time = ''
                }
            }

            if (array['style'] == 'timeout' || array['style'] == 'early_timeout') {
                if (time) {
                time = this.formatTime(time, formatterParams, onRendered)
                } else {
                time = ''
                }

                if (array['style'] == 'early_timeout') {
                time = time + " <span class='dsq'>E </span>"
                }
                //time = "<span class='dsq'>"+time+"</span>";
            }

            if (array['style'] == 'incomplete') {
                time = "<span class='incomplete'>(" + time + ')</span>'
            }

            if (array['style'] == 'unscored') {
                time = "<span class='unscored'>" + time + '</span>'
            }

            if (array['icon'] != '') {
                var icon = "<i class='" + array['icon'] + "'></i>"
                if (array['iconPosition'] == 'left') {
                time = icon + '' + time
                } else if (array['iconPosition'] == 'right') {
                time = time + '' + icon
                } else if (array['iconPosition'] == 'kill') {
                time = icon
                }
            }
            return time
        },
        displayTcPenalty(cell) {
            var array = cell.getValue()
            var time = array['score']

            if (!time) {
                time = '0'
            }

            if (array['style'] == 'dsq') {
                time = "<span class='dsq'>" + time + '</span>'
            } else {
                if (array['style'] == 'early_dsq') {
                time = "<span class='dsq'>" + time + ' E</span>'
                }
                if (array['style'] == 'late_dsq') {
                time = "<span class='dsq'>" + time + ' L</span>'
                }
            }

            if (array['style'] == 'timeout' || array['style'] == 'early_timeout') {
                time = ''

                if (array['style'] == 'early_timeout') {
                time = "<span class='dsq'>E </span>"
                } else {
                time = "<span class='dsq'></span>"
                }
            }

            if (array['style'] == 'incomplete') {
                time = ''
            }

            if (array['style'] == 'unscored') {
                time = ''
            }

            if (array['icon'] != '') {
                var icon = "<i class='" + array['icon'] + "'></i>"
                if (array['iconPosition'] == 'left') {
                time = icon + '' + time
                } else if (array['iconPosition'] == 'right') {
                time = time + '' + icon
                } else if (array['iconPosition'] == 'kill') {
                time = icon
                }
            }
            return time
        },
        //--------------------------
        async updateScores() {
            try {
                await ResultsService.getRaceInfo(this.raceId).then(response =>{
                    const hash = response.data.message.Score_Hash;
                    this.scoreHash = hash;
                    var table = this.$refs.tabulator.getInstance();

                    table.replaceData();
                });
            } catch(e) {
                this.msg_snackbar = this.$t('common.error_general');
                this.color_snackbar = 'error';
                this.snackbar = true;
            }
        },
        toggleDetails() {
            var table = this.$refs.tabulator;
            var i = 0;
            if(table) {
                table = table.getInstance();
                if (this.selectedSections.includes(-1)) {
                    this.selectedSections = []

                    for (i = 0; i < this.sectionItems.length; i++) {
                        if (this.sectionItems[i].value == -1) continue
                            this.selectedSections.push(this.sectionItems[i].value)
                    }
                }
                table.blockRedraw()
                var cols = table.getColumnDefinitions()
                //var _data = this.tabulatorData;
                /*console.log('_data',_data); 
                var _sections = _data[0].sections;*/
                var sectCols = []
                //get coltypes
                for (i = 0; i < cols.length; i++) {
                    if (typeof cols[i].columns != 'undefined') {
                        let type = 's'
                        for (var j = 0; j < cols[i].columns.length; j++) {
                            if (cols[i].columns[j].field.includes('recalKeyTimeIn')) {
                                type = 'tc'
                            }
                        }
                        sectCols.push(type)
                    }
                }
                let colsField = [];
                cols.forEach((el) => {
                    if(el.field) {
                        colsField.push(el.field);
                    }
                    if(el.columns) {
                        el.columns.forEach((el1) => {
                            colsField.push(el1.field);
                        });
                    }
                });
                for (i = 0; i < this.sectionItems.length; i++) {
                    const section = this.sectionItems[i].value
                    //const section_name = this.sectionItems[i].text
                    if (section > -1) {
                        if (this.selectedSections.includes(section)) {
                            if (sectCols[section] == 's') {
                                if (this.raceInfo.Race_Mode == 10 && this.raceInfo.Race_Submode == 'sprint') {
                                    if(colsField.includes(`sections.${section}.totalTime`)) table.hideColumn(`sections.${section}.totalTime`);
                                    if(colsField.includes(`sections.${section}.totalTimeSection`)) table.showColumn(`sections.${section}.totalTimeSection`)
                                    if(colsField.includes(`sections.${section}.details`)) table.showColumn(`sections.${section}.details`)
                                } else if (this.raceInfo.Race_Mode == 6 || this.raceInfo.Race_Mode == 9) {
                                    if(colsField.includes(`sections.${section}.overallPosition`)) table.showColumn(`sections.${section}.overallPosition`)
                                    if(colsField.includes(`sections.${section}.classPosition`)) table.showColumn(`sections.${section}.classPosition`)
                                    if(colsField.includes(`sections.${section}.readTimeIn`)) table.showColumn(`sections.${section}.readTimeIn`) //prevLap
                                    if(colsField.includes(`sections.${section}.readTimeOut`)) table.showColumn(`sections.${section}.readTimeOut`)
                                    if(colsField.includes(`sections.${section}.totalSectionTime`)) table.showColumn(`sections.${section}.totalSectionTime`)
                                } else {
                                    if(colsField.includes(`sections.${section}.keyTimeIn`)) table.showColumn(`sections.${section}.keyTimeIn`)
                                    if(colsField.includes(`sections.${section}.readTimeIn`)) table.showColumn(`sections.${section}.readTimeIn`)
                                    if(colsField.includes(`sections.${section}.penaltyIn`)) table.showColumn(`sections.${section}.penaltyIn`)
                                    if(colsField.includes(`sections.${section}.keyTimeOut`)) table.showColumn(`sections.${section}.keyTimeOut`)
                                    if(colsField.includes(`sections.${section}.readTimeOut`)) table.showColumn(`sections.${section}.readTimeOut`)
                                    if(colsField.includes(`sections.${section}.penaltyOut`)) table.showColumn(`sections.${section}.penaltyOut`)
                                    if(colsField.includes(`sections.${section}.totalSectionTime`)) table.showColumn(`sections.${section}.totalSectionTime`)
                                }
                            }

                            if (sectCols[section] == 'tc') {
                                /* timecheck */
                                if (!this.bandReliability) {
                                    if(colsField.includes(`sections.${section}.totalTimeArray`)) table.hideColumn(`sections.${section}.totalTimeArray`)
                                }

                                if(colsField.includes(`sections.${section}.recalKeyTimeIn`)) table.showColumn(`sections.${section}.recalKeyTimeIn`)
                                if(colsField.includes(`sections.${section}.readTime`)) table.showColumn(`sections.${section}.readTime`)
                                if(colsField.includes(`sections.${section}.tcPenalties`)) table.showColumn(`sections.${section}.tcPenalties`)
                                if(colsField.includes(`sections.${section}.keyTimeDeltaOut`)) table.showColumn(`sections.${section}.keyTimeDeltaOut`)
                            }
                        } else {
                            if (sectCols[section] == 's') {
                                if (this.raceInfo.Race_Mode == 10 && this.raceInfo.Race_Submode == 'sprint') {
                                    if(colsField.includes(`sections.${section}.totalTime`)) table.showColumn(`sections.${section}.totalTime`);
                                    if(colsField.includes(`sections.${section}.totalTimeSection`)) table.hideColumn(`sections.${section}.totalTimeSection`)
                                    if(colsField.includes(`sections.${section}.details`)) table.hideColumn(`sections.${section}.details`)
                                } else if (this.raceInfo.Race_Mode == 6 || this.raceInfo.Race_Mode == 9) {
                                    if(colsField.includes(`sections.${section}.overallPosition`)) table.hideColumn(`sections.${section}.overallPosition`)
                                    if(colsField.includes(`sections.${section}.classPosition`)) table.hideColumn(`sections.${section}.classPosition`)
                                    if(colsField.includes(`sections.${section}.readTimeIn`)) table.hideColumn(`sections.${section}.readTimeIn`)
                                    if(colsField.includes(`sections.${section}.readTimeOut`)) table.hideColumn(`sections.${section}.readTimeOut`)
                                    if(colsField.includes(`sections.${section}.totalSectionTime`)) table.hideColumn(`sections.${section}.totalSectionTime`)
                                } else {
                                    if(colsField.includes(`sections.${section}.keyTimeIn`)) table.hideColumn(`sections.${section}.keyTimeIn`)
                                    if(colsField.includes(`sections.${section}.readTimeIn`)) table.hideColumn(`sections.${section}.readTimeIn`)
                                    if(colsField.includes(`sections.${section}.penaltyIn`)) table.hideColumn(`sections.${section}.penaltyIn`)
                                    if(colsField.includes(`sections.${section}.keyTimeOut`)) table.hideColumn(`sections.${section}.keyTimeOut`)
                                    if(colsField.includes(`sections.${section}.readTimeOut`)) table.hideColumn(`sections.${section}.readTimeOut`)
                                    if(colsField.includes(`sections.${section}.penaltyOut`)) table.hideColumn(`sections.${section}.penaltyOut`)
                                    if(colsField.includes(`sections.${section}.totalSectionTime`)) table.hideColumn(`sections.${section}.totalSectionTime`)
                                }
                            }

                            if (sectCols[section] == 'tc') {
                                /**timecheck */
                                if (!this.bandReliability) {
                                    if(colsField.includes(`sections.${section}.totalTimeArray`)) table.showColumn(`sections.${section}.totalTimeArray`)
                                }

                                if(colsField.includes(`sections.${section}.recalKeyTimeIn`)) table.hideColumn(`sections.${section}.recalKeyTimeIn`)
                                if(colsField.includes(`sections.${section}.readTime`)) table.hideColumn(`sections.${section}.readTime`)
                                if(colsField.includes(`sections.${section}.tcPenalties`)) table.hideColumn(`sections.${section}.tcPenalties`)
                                if(colsField.includes(`sections.${section}.keyTimeDeltaOut`)) table.hideColumn(`sections.${section}.keyTimeDeltaOut`)
                            }
                        }
                    }
                }
                
                table.restoreRedraw();
                if (this.raceInfo.Race_Mode == 10 && this.raceInfo.Race_Submode == 'sprint') {
                    table.redraw(true)
                }
            }
        },
        tabulatorAjaxResponse(url, params, response){
            this.tabulatorData = response.data;
            this.setDataFilters(response.data);
            if(response.last_page && response.last_page == response.current_page){
                setTimeout(() => {
                    var table = this.$refs.tabulator.getInstance();
                    table.setSort("overallPosition", "asc");
                }, 4000);
            }
            if(!url.includes('undefined') && this.tabulatorData.length == 0) {
                let placeholder = document.getElementsByClassName('placeholderTabulator');
                if(placeholder.length > 0) {
                    placeholder = placeholder[0];
                    placeholder.classList.remove("d-none");
                }
            }
            if(this.isShowingOverall) {
                this.verifyOverallFilter();
            }
            if(this.isModeSprint) {
                const { data } = response;
                if(typeof data !== 'undefined') {
                    return data;
                }
            }
            
            return response;
        },
        verifyOverallFilter() {
            const table = this.$refs.tabulator?.getInstance();
            const filtersActive = table.getFilters().map(filter => filter.field);
            if(!filtersActive.includes('overallPosition')) {
                table.setFilter([
                    {
                        field: 'overallPosition',
                        type: '>',
                        value: '0',
                    }
                ]);
            }
        },
        setDataFilters(newData) {
            var participants = [];
            var brands = [];
            var groups = [];
            var cl;
            for (var i = 0; i < newData.length; i++) {
                //participants
                participants.push({
                    text: `${newData[i].firstName} ${newData[i].lastName} - ${newData[i].displayedNumber}`,
                    value: newData[i].id,
                });

                //brands
                if (newData[i].brand) {
                    cl = {
                        text: newData[i].brand,
                        value: newData[i].brand,
                    };

                    if (!brands.includes(cl)) {
                        brands.push(cl);
                    }
                }

                //groups
                if (newData[i].classGroup) {
                    cl = {
                        text: newData[i].classGroup,
                        value: newData[i].classGroup,
                    };

                    if (!groups.includes(cl)) {
                        groups.push(cl);
                    }
                }
            }
            this.participantNames = this.sortArrays(this.participantNames.concat(participants));
            this.brandNames = this.sortArrays(this.brandNames.concat(brands));
            this.groupNames = this.sortArrays(this.groupNames.concat(groups));
        },
        sortArrays(arrays) {
            return arrays.slice().sort(function (a, b) {
                return a.text > b.text ? 1 : -1;
            });
        },
        getResults(){
            if(this.raceClasses.length > 0){
                this.isLoading = false;
            }
        },
        setTimeFormat() {
            this.formatWasChanged = true;

            var table = this.$refs.tabulator.getInstance();

            this.timeFormatSelected = this.timeFormatSelected == 0 ? 1 : 0;

            var rows = table.getRows();
            rows.forEach((row) => {
                row.reformat();
            });
        },
        getSortOrder(className) {
            let sortOrder = 0;

            for (var i = 0; i < this.raceClasses.length; i++) {
                if (this.raceClasses[i].name != className) continue;
                sortOrder = this.raceClasses[i].sortOrder;
            }

            return sortOrder;
        },
        setNullToZero(participantIndex, sectionIndex) {
            for (var j = 0; j < this.setZeroForNullColumns.length; j++) {
                //set specific fields, its value to 0 when the value is equals to null
                if (
                typeof this.raceResults[participantIndex].sections[sectionIndex][
                    this.setZeroForNullColumns[j]
                ] == "object"
                )
                this.raceResults[participantIndex].sections[sectionIndex][
                    this.setZeroForNullColumns[j]
                ] = 0;
            }
        },
        participantsAreEmpty() {
            if (this.$refs.tabulator) {
                var table = this.$refs.tabulator.getInstance();
                if (table.getData().length == 0) {
                return true;
                }
            }

            return false;
        },
        anyFiltersActive() {
            if (
                this.selectedParticipants.length == 0 &&
                this.selectedClasses.length == 0 &&
                this.selectedBrands.length == 0 &&
                this.selectedGroups.length == 0
            ) {
                return false;
            }

            return true;
        },
        sortFilters(data) {
            data.sort((a, b) => (a.text > b.text ? 1 : b.text > a.text ? -1 : 0));
        },
        downloadCsv() {
            const table = this.$refs.tabulator.getInstance();
            table.download("csv", "data.csv", {
                delimiter: ",",
            });
        },
        print() {
            const url = `${this.$store.getters.env.get('VUE_APP_DOMAIN')}/livelaps/scores/print/${this.raceInfo.raceId}`;
            window.open(url, "_blank");
        },
        presentation() {
            var table = this.$refs.tabulator.getInstance();
            if(this.presenting){
                this.innerHeight = window.innerHeight - 250;
                this.presenting = false;

                this.selectedClasses = [];
                this.updateClassFilters();
            } else {
                this.innerHeight = window.innerHeight - 130;
                this.presenting = true;
            }
            table.setHeight(this.innerHeight);

            table.setSort("overallPosition", "asc");
            table.redraw(true);

            if(this.presenting){
                this.presentingIndex = 0;
                this.animateTable(0);
            }
        },
        animateTable(inc){
            //get all classes
            var classes = this.classesNames;

            if(inc == 1){
                this.presentingIndex += 1;
            }

            var ci = this.presentingIndex;


            //if no classes exist, cry (return false)
            if(classes.length == 0 || !this.presenting){
                return false;
            }
            //set class by index passed (ci)
            //if class doesnt exist, call

            if(typeof classes[ci] == 'undefined'){
                this.presentingIndex = 0;
                return this.animateTable(0);
            } else {
                this.selectedGroups = [];
                this.selectedBrands = [];
                this.selectedClasses = [];
                this.selectedParticipants = [];
                this.selectedSections = [];
                this.selectedClasses.push(classes[ci].value);
                this.updateClassFilters();
                this.updateFilters();
                this.toggleDetails();
            }
        },
        moveToNextClass(){
            setTimeout(() => this.animateTable(1), 500);
        },
        addSelectedParticipants(participants, data) {
            for (var i = 0; i < this.selectedParticipants.length; i++) {
                for (var j = 0; j < data.length; j++) {
                const p = data[j];
                if (p.id == this.selectedParticipants[i]) {
                    participants.push(p.id);
                    break;
                }
                }
            }
            return participants;
        },
        addParticipantsInSelectedClasses(participants, data) {
            for (var i = 0; i < this.selectedClasses.length; i++) {
                for (var j = 0; j < data.length; j++) {
                    const p = data[j];
                    if (
                        p.className == this.selectedClasses[i] &&
                        !participants.includes(p.id)
                    ) {
                        participants.push(p.id);
                    }
                }
            }
            return participants;
        },
        addParticipantsInSelectedBrands(participants, data) {
            for (var i = 0; i < this.selectedBrands.length; i++) {
                for (var j = 0; j < data.length; j++) {
                    const p = data[j];
                    if (
                        p.brand == this.selectedBrands[i] &&
                        !participants.includes(p.id)
                    ) {
                        participants.push(p.id);
                    }
                }
            }
            return participants;
        },
        addParticipantsInSelectedGroups(participants, data) {
            for (var i = 0; i < this.selectedGroups.length; i++) {
                for (var j = 0; j < data.length; j++) {
                    const p = data[j];
                    if (
                        p.classGroup == this.selectedGroups[i] &&
                        !participants.includes(p.id)
                    ) {
                        participants.push(p.id);
                    }
                }
            }
            return participants;
        },
        clickSearch() {
            this.search = this.lastSearch;
            this.bandChange = false;
        },
        selectAllSections() {
            this.$nextTick(() => {
                if (this.likesAllSection) {
                this.selectedSections = []
                } else {
                    this.sectionItems.forEach(elem =>{
                        if(!this.selectedSections.includes(elem.value)) this.selectedSections.push(elem.value);
                    })
                }
                this.toggleDetails();
            });
        },
        setBehindColumn() {
            var table = this.$refs.tabulator.getInstance();

            if (this.selectedClasses.length > 0) {
                table.hideColumn("overallBehindBy");
                table.showColumn("classBehindBy");
            } else {
                table.hideColumn("classBehindBy");
                table.showColumn("overallBehindBy");
            }
            return true;
        },
        resetFilters() {
            this.participantNames = this.brandNames = this.groupNames = [];
            this.selectedParticipants = [];
            this.selectedClasses = [];
            this.selectedBrands = [];
            this.selectedGroups = [];
            this.selectedSections = [];
        },
        replaceFind(match) {
            return '<span style="color: #808080;">' + match + '</span>';
        },
        searchCacheHandler(search) {
            /**
             * Store data in the search cache
             * only what the user writes will be cached here
             */
            if(search){
    
                if(search.length == 1 && !this.lastSearch) {
                    this.lastSearch = search;
                }
    
                if(this.lastSearch &&  this.lastSearch.length == (search.length - 1)) {
                    this.lastSearch = search;
                }
    
                if(this.lastSearch &&  this.lastSearch.length == (search.length + 1)) {
                    this.lastSearch = search;
                }
            }
    
            if(!search && this.lastSearch &&  this.lastSearch.length == 1)
            {
                this.lastSearch = search;
            }
        },
        expandSprintDefault() {
            this.sectionItems.forEach(elem =>{
                if(!this.selectedSections.includes(elem.value)) this.selectedSections.push(elem.value);
            });
            const table = this.$refs.tabulator?.getInstance();
            this.sectionItems.forEach((section) => {
                table.showColumn(`sections.${section.value}.totalTimeSection`);
                for(let j = 0; j < section.runLimit; j++) {
                    table.showColumn(`sections.${section.value}.details.${j}`);
                    if(this.raceInfo?.penaltyColumn) {
                        table.showColumn(`sections.${section.value}.details.${j}.penalties`);
                    }
                }
            });
            setTimeout(() => {
                table.redraw(true);
            },500);
        }
    },
    watch: {
        async search(val) {

            this.searchCacheHandler(val);

            /**
             * Store data in the search cache
             * the earch input can be cached only if, the input has data, and the actual cache size is equals to the actual data.length +-1
             */
            if(val && val.length == 1 && !this.lastSearch) {
                this.lastSearch = val;
            }

            if((val || "").length >= 3)
            {
                if (this.isLoading) return
                this.isLoading = true
                RegisteredParticipantsService.findEvents({ search: this.search.toLowerCase(), type: 'race' }).then(response => {
                    this.races = response.data.message.map((item) => {
                        return {
                            ...item,
                            text: `${item.eventDate} ${item.promoterName} ${item.eventName} ${item.raceName}`
                        };
                    });
                    this.isLoading = false;
                    this.sortBy = "createdDate";
                    this.sortDesc = false;
                }).catch(() => {
                    this.isLoading = false;
                });
            } else {
                this.isLoading = false;
                this.races = [];
            }
        },
        selectedClasses(newValue) {
            if(newValue.length > 0) {
                const table = this.$refs.tabulator?.getInstance();
                const filters = table.getFilters().map(filter => filter.field);
                if(!filters.includes('overallPosition')) {
                    table.removeFilter('overallPosition');
                }
            } else if(this.isShowingOverall && !this.isLoading) {
                this.verifyOverallFilter();
            }
        }
    }
}
</script>

<style lang='scss'>
//brand colors
$yamaha: #000099;
$ktm: #ff6600;
$honda: #e00031;
$suzuki: #ebd623;
$kawasaki: #33cc00;
$gasgas: #e63429;
$sherco: #0606a8;
$beta: #e60000;
$husqvarna: #002550;
$husaberg: #024fa7;
$tm: #00a2d8;
#resultsDetailsPage {
.btn{ color: #fff !important; }
.theme--dark.v-application { color: black;  }
.tabulator-page-size { 
    -moz-appearance: menulist !important;   -webkit-appearance: menulist !important;  background-color: white; 
}
body { background-color: white;  }
:root h1 { --color: red; display: flex; color: var(--color); }
.isthrown .tabulator-col-title { color: red; text-decoration: line-through; }
.isthrown .tabulator-col-group-cols .tabulator-col-title { color: white; text-decoration: none; }
.dsq { font-weight: bold; color: red; }
.milliseconds, .section-posittion { font-size: 10px; }
.fared::before, .fared { color: #f00;  }
.tabulator.table-striped.table-bordered.table-sm {  font-size: 13px; }
.tabulator.table-bordered .tabulator-header .tabulator-col { background-color: #666; color: white; }
.tabulator.table-bordered { border: 0px; }
.tabulator .tabulator-header { border-bottom: 0px; border-top: 0px; background: #495464 !important; }
.isExcludedDisplayed { display: none !important; }
.faredarrow::before, .faredarrow { color: #f00; font-size: 14px; }
.fagray::before, .fagray { color: #666; font-size: 10px; }
.text-blue { color: #002550; }
.text-small { font-size: 11px; font-weight: bold; }
.incomplete { color: #002550; font-size: 0.7em; }
.unscored { color: #002550; font-size: 0.7em; }
.fill { width: 100%; height: 100%; }
.sectionPositionText { font-size: 0.8em; }
.bold { font-weight: 600; }
.yamaha { background-color: $yamaha; color: #fff; }
.ktm { background-color: $ktm; color: #fff; }
.honda { background-color: $honda; color: #fff; }
.suzuki { background-color: $suzuki; color: #fff; }
.kawasaki { background-color: $kawasaki; color: #fff; }
.gasgas { background-color: $gasgas; color: #fff; }
.sherco { background-color: $sherco; color: #fff; }
.beta { background-color: $beta; color: #fff; }
.husqvarna { background-color: $husqvarna; color: #fff; }
.husaberg, .husaburg { background-color: $husaberg; color: #fff; }
.tm { background-color: $tm; color: #fff; }
.tabulator-cell { font-size: 13px; }
.tabulator-cell.tabulator-frozen.tabulator-frozen-left { background-color: white; }
.tabulator-col-content { 
    padding: 3px !important; font-size: 13px !important; text-align: center !important; 
}
.tabulator-cell { 
    padding: 0px !important; margin: 0px !important; border-left: 1px solid #6666660a; 
    border-right: 1px solid #6666660a;
}
.bigfilters { margin-top: 50px; margin-bottom: 0px; padding-bottom: 0px; height: auto; }
.tabulator-row-odd { background-color: #eaeaea33; }
.remove-round { border-radius: 0px !important; }
.remove-round .v-card__title.headline { padding-bottom: 0px; }
header, #header2 { 
    height: 110px; -webkit-box-shadow: 0px 0px 3px 0px #999; box-shadow: 0px 0px 3px 0px #999;
    margin-bottom: 1px;
}
#backButton { 
    position: absolute; top: 10px; left: 18px; color: #ccc; cursor: pointer; text-decoration: none;
    z-index: 1000;
}
#raceImage {
  position: absolute; top: 40px; left: 18px; height: 113px; z-index: 11; width: 150px; padding: 2px;
  border: 1px solid #aaa; border-radius: 3px; background-color: #fff;
}
#raceImage img { max-width: 100%; max-height: 100%; }
#raceDetails {
  position: absolute; top: 0px; left: 200px; padding: 5px; padding-left: 5px; padding-left: 10px;
  color: #fff;
}
#raceDetails #name {
  font-size: 24px; color: #fff; margin-right: 20px; max-width: 475px; overflow: hidden;
}
#raceDetails #event { color: #fff; margin-top: 7px; }
#raceDetails #promoter { color: #fff; margin-top: 7px; }
#topAd {
  position: fixed; top: 3px; right: 310px; height: 90px; width: 600px; max-width: 600px; max-height: 90px;
}
#optionWrapper { top: 8px; right: 15px; position: absolute; width: 290px; }
.option { 
    cursor: pointer; font-size: 11px; color: #fff; background-repeat: no-repeat;
    padding-left: 21px; margin-bottom: 6px; padding-top: 4px; height: 12px;
}
#filterPanel {
  position: relative; z-index: 10; padding: 2px 0 20px 200px; width: 100%; -webkit-box-sizing: border-box;
  box-sizing: border-box; height: 70px;
}
#loading-wrapper {
  position: fixed; width: 100%; height: 100%; left: 0; top: 0; background: rgba(0,0,0,0.5);
  z-index: 10;
}
#loading-text{
  display: block; position: absolute; top: 50%; left: 50%; color: #fff; width: 100px;
  height: 30px; margin:-7px 0 0 -45px; text-align: center; font-size: 20px;
}
#loading-content{
  display: block; position: relative; left: 50%; top: 50%; width: 170px; height: 170px;
  margin: -85px 0 0 -85px; border:3px solid #f00;
}
#loading-content{
  border:3px solid transparent; border-top-color: #fff; border-bottom-color:#fff;
  border-radius: 50%; -webkit-animation: loader 2s linear infinite; 
  -moz-animation: loader 2s linear infinite; -o-animation: loader 2s linear infinite;
  animation: loader 2s linear infinite;
}
.tabulator-col-group-cols { height: auto !important; }
}
@-webkit-keyframes loader{
  0%{
    -webkit-transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100%{
    -webkit-transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>